// External dependencies
import L from 'leaflet';
import React, { useEffect, useRef } from 'react';
import Supercluster from 'supercluster';

import './leaflet-search-map.less';

// Internal dependencies
import { getRasterMapTiles } from '@/modules/core/client/utils/map';
import { CLUSTER_MAX_ZOOM, MIN_ZOOM } from './constants';

const offerColours = {
  'host-maybe': '#f2ae43',
  'host-yes': '#58ba58',
  'meet-yes': '#11b4da',
};
type OfferColourKey = keyof typeof offerColours;

interface LeafletViewport {
  latitude: number;
  longitude: number;
  zoom: number;
}

interface LeafletPointProperties {
  id: string;
  offer?: OfferColourKey | null;
  cluster?: never;
  cluster_id?: never;
  point_count?: never;
  point_count_abbreviated?: never;
  [key: string]: unknown;
}

interface LeafletPointCollection {
  features: GeoJSON.Feature<GeoJSON.Point, LeafletPointProperties>[];
}

interface LeafletPointClickFeature {
  type: 'Feature';
  id?: string | number;
  geometry: GeoJSON.Point;
  properties: LeafletPointProperties;
}

interface LeafletMapBounds {
  northEast: { lat: number; lng: number };
  southWest: { lat: number; lng: number };
}

interface LeafletSearchMapProps {
  onInteraction?: () => void;
  bounds?: LeafletMapBounds | null;
  communityNotes: LeafletPointCollection;
  offers: LeafletPointCollection;
  onCommunityNoteClick: (feature: LeafletPointClickFeature) => void;
  onMapChange: (
    state: ReturnType<typeof getMapState>,
    explicitPlaceFit?: boolean,
  ) => void;
  onMapClick: () => void;
  onOfferClick: (offerId: string) => void;
  viewport: LeafletViewport;
}

interface LeafletLayerGroups {
  communityNoteGroup: L.LayerGroup;
  offerGroup: L.LayerGroup;
}

interface MarkerOptions {
  colour: string;
  group: L.LayerGroup;
  index: Supercluster<LeafletPointProperties, Record<string, unknown>>;
  map: L.Map;
  onPointClick: (point: LeafletPointClickFeature) => void;
  pointColour: (point: LeafletPointClickFeature) => string;
  viewport: LeafletViewport;
}

function getMapState(map: L.Map) {
  const bounds = map.getBounds();
  const centre = map.getCenter();

  return {
    bounds: {
      northEast: bounds.getNorthEast(),
      southWest: bounds.getSouthWest(),
    },
    latitude: centre.lat,
    longitude: centre.lng,
    zoom: map.getZoom(),
  };
}

function createClusterIndex(data: LeafletPointCollection, maxZoom: number) {
  return new Supercluster<LeafletPointProperties, Record<string, unknown>>({
    maxZoom,
    minPoints: 3,
    radius: 50,
  }).load(data.features);
}

function stopMapClick(event: L.LeafletMouseEvent) {
  if (event?.originalEvent) {
    L.DomEvent.stopPropagation(event.originalEvent);
  }
}

function clusterIcon(count: string | number, colour: string) {
  return L.divIcon({
    className: 'leaflet-search-cluster',
    html: `<span style="background:${colour}">${count}</span>`,
    iconAnchor: [25, 25],
    iconSize: [50, 50],
  });
}

function addClusteredMarkers({
  colour,
  group,
  index,
  map,
  onPointClick,
  pointColour,
  viewport,
}: MarkerOptions) {
  group.clearLayers();

  if (viewport.zoom <= MIN_ZOOM) {
    return;
  }

  const bounds = map.getBounds();
  const points = index.getClusters(
    [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
    Math.floor(viewport.zoom),
  );

  points.forEach(point => {
    const [longitude, latitude] = point.geometry.coordinates;
    const clusterId = point.properties.cluster_id;

    if (point.properties.cluster && typeof clusterId === 'number') {
      const marker = L.marker([latitude, longitude], {
        bubblingMouseEvents: false,
        icon: clusterIcon(point.properties.point_count_abbreviated, colour),
        keyboard: true,
        title: `${point.properties.point_count} results`,
      });

      marker.on('click', (event: L.LeafletMouseEvent) => {
        stopMapClick(event);
        map.setView(
          [latitude, longitude],
          Math.min(index.getClusterExpansionZoom(clusterId), CLUSTER_MAX_ZOOM),
        );
      });
      group.addLayer(marker);
      return;
    }

    const marker = L.circleMarker([latitude, longitude], {
      bubblingMouseEvents: false,
      color: '#fff',
      fillColor: pointColour(point as LeafletPointClickFeature),
      fillOpacity: 1,
      radius: 10,
      weight: 2,
    });
    marker.on('click', event => {
      stopMapClick(event);
      onPointClick(point as LeafletPointClickFeature);
    });
    group.addLayer(marker);
  });
}

/**
 * Search-map renderer used where a WebGL map cannot be created.
 */
export default function LeafletSearchMap({
  bounds,
  communityNotes,
  offers,
  onCommunityNoteClick,
  onMapChange,
  onMapClick,
  onOfferClick,
  onInteraction,
  viewport,
}: LeafletSearchMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const explicitPlaceFitRef = useRef(false);
  const mapRef = useRef<L.Map | null>(null);
  const groupsRef = useRef<LeafletLayerGroups | null>(null);
  const callbacksRef = useRef({
    onCommunityNoteClick,
    onMapChange,
    onMapClick,
    onOfferClick,
  });

  useEffect(() => {
    callbacksRef.current = {
      onCommunityNoteClick,
      onMapChange,
      onMapClick,
      onOfferClick,
    };
  }, [onCommunityNoteClick, onMapChange, onMapClick, onOfferClick]);

  useEffect(() => {
    const container = containerRef.current!;

    const map = L.map(container, {
      zoomControl: false,
    }).setView([viewport.latitude, viewport.longitude], viewport.zoom);
    L.control.zoom({ position: 'topright' }).addTo(map);
    const offerGroup = L.layerGroup().addTo(map);
    const communityNoteGroup = L.layerGroup().addTo(map);

    const tiles = getRasterMapTiles();
    L.tileLayer(tiles.url, tiles.options).addTo(map);

    const onMoveEnd = () => {
      callbacksRef.current.onMapChange(
        getMapState(map),
        explicitPlaceFitRef.current,
      );
      explicitPlaceFitRef.current = false;
    };
    map.on('click', () => callbacksRef.current.onMapClick());
    map.on('moveend', onMoveEnd);
    mapRef.current = map;
    groupsRef.current = { communityNoteGroup, offerGroup };
    onMoveEnd();

    // The search pane changes size after the mobile controls are laid out.
    // Leaflet otherwise keeps the narrow initial viewport and leaves unfilled
    // space beside the requested tiles on iOS.
    const invalidateSize = () => map.invalidateSize({ pan: false });
    const initialResize = window.setTimeout(invalidateSize);
    const settledResize = window.setTimeout(invalidateSize, 250);
    window.addEventListener('resize', invalidateSize);

    return () => {
      window.clearTimeout(initialResize);
      window.clearTimeout(settledResize);
      window.removeEventListener('resize', invalidateSize);
      map.remove();
      groupsRef.current = null;
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    /* istanbul ignore next -- the map ref is initialised by the preceding mount effect */
    if (!map) {
      return;
    }

    // A selected place owns the camera until its bounds are cleared. Without
    // this guard, a render carrying the old persisted viewport can undo
    // fitBounds before Leaflet's moveend publishes the fitted viewport.
    if (bounds?.northEast && bounds?.southWest) {
      return;
    }

    const centre = map.getCenter();
    if (
      centre.lat !== viewport.latitude ||
      centre.lng !== viewport.longitude ||
      map.getZoom() !== viewport.zoom
    ) {
      map.setView([viewport.latitude, viewport.longitude], viewport.zoom);
    }
  }, [bounds, viewport]);

  // Leaflet needs to fit external search bounds itself. Deriving a viewport
  // through the WebGL map utility leaves the raster map with stale dimensions
  // after the mobile place-search panel has closed.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !bounds?.northEast || !bounds?.southWest) {
      return;
    }

    const fitSelectedBounds = () => {
      map.invalidateSize({ pan: false });
      explicitPlaceFitRef.current = true;
      map.fitBounds(
        [
          [bounds.southWest.lat, bounds.southWest.lng],
          [bounds.northEast.lat, bounds.northEast.lng],
        ],
        { padding: [40, 40] },
      );
    };

    const container = containerRef.current!;

    // Fit immediately, then again after the mobile place sidebar is hidden
    // panel and the map container has completed its layout transition.
    fitSelectedBounds();
    const nextLayoutFit = window.setTimeout(fitSelectedBounds);
    const settledLayoutFit = window.setTimeout(fitSelectedBounds, 250);

    const cancelDeferredFits = () => {
      window.clearTimeout(nextLayoutFit);
      window.clearTimeout(settledLayoutFit);
    };

    // Once someone starts moving or zooming the map, their camera choice wins
    // over the delayed mobile-layout correction below.
    container.addEventListener('pointerdown', cancelDeferredFits);
    container.addEventListener('touchstart', cancelDeferredFits);
    container.addEventListener('wheel', cancelDeferredFits);

    return () => {
      cancelDeferredFits();
      container.removeEventListener('pointerdown', cancelDeferredFits);
      container.removeEventListener('touchstart', cancelDeferredFits);
      container.removeEventListener('wheel', cancelDeferredFits);
    };
  }, [bounds]);

  useEffect(() => {
    const map = mapRef.current;
    const groups = groupsRef.current;
    /* istanbul ignore next -- both refs are initialised by the preceding mount effect */
    if (!map || !groups) {
      return;
    }

    const offerIndex = createClusterIndex(offers, CLUSTER_MAX_ZOOM);
    addClusteredMarkers({
      colour: 'rgba(18, 181, 145, 0.8)',
      group: groups.offerGroup,
      index: offerIndex,
      map,
      onPointClick: point =>
        callbacksRef.current.onOfferClick(point.properties.id),
      pointColour: point =>
        (point.properties.offer && offerColours[point.properties.offer]) ||
        '#ccc',
      viewport,
    });

    const communityNoteIndex = createClusterIndex(communityNotes, 14);
    addClusteredMarkers({
      colour: 'rgba(25, 118, 210, 0.8)',
      group: groups.communityNoteGroup,
      index: communityNoteIndex,
      map,
      onPointClick: point => callbacksRef.current.onCommunityNoteClick(point),
      pointColour: () => '#1565C0',
      viewport,
    });
  }, [communityNotes, offers, viewport]);

  return (
    <div
      className="search-map leaflet-search-map"
      data-testid="leaflet-search-map"
      data-map-zoom={viewport.zoom}
      data-map-latitude={viewport.latitude}
      data-map-longitude={viewport.longitude}
      onPointerDownCapture={onInteraction}
      onClickCapture={onInteraction}
      onTouchStartCapture={onInteraction}
      onWheelCapture={onInteraction}
      onKeyDownCapture={onInteraction}
      ref={containerRef}
    />
  );
}
